import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Avatar,
  Alert,
  Divider
} from '@mui/material';
import {
  Search,
  MoreVert,
  Edit,
  Block,
  CheckCircle,
  Delete,
  Visibility,
  FilterList,
  PersonAdd,
  DeleteForever
} from '@mui/icons-material';
import axios from 'axios';

interface User {
  id: string;
  memberId?: string;
  email: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  role: string;
  status: string;
  phoneVerified: boolean;
  emailVerified: boolean;
  driverRating: number;
  passengerRating: number;
  totalTripsAsDriver: number;
  totalTripsAsPassenger: number;
  createdAt: string;
  lastLoginAt: string;
  profilePicture?: string;
}

interface UserDetailsData extends User {
  vehicles?: any[];
  ridesAsDriver?: any[];
  bookingsAsPassenger?: any[];
  documents?: any[];
  supportTickets?: any[];
  disputesReported?: any[];
  disputesAgainst?: any[];
}

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetailsData | null>(null);
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [permanentDeleteDialog, setPermanentDeleteDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    email: '',
    phoneNumber: '',
    role: '',
    status: ''
  });
  const [createFormData, setCreateFormData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    email: '',
    phoneNumber: '',
    password: '',
    role: 'PASSENGER',
    status: 'ACTIVE'
  });

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, search, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        ...(search && { search }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { status: statusFilter })
      });

      const response = await axios.get(`${apiUrl}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(response.data.users);
      setTotalUsers(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      showAlert('error', 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetails(response.data.user);
      setDetailsDialog(true);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      showAlert('error', 'Failed to load user details');
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${apiUrl}/admin/users/${selectedUser.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', `User status updated to ${newStatus}`);
      setStatusDialog(false);
      fetchUsers();
    } catch (error) {
      console.error('Failed to update status:', error);
      showAlert('error', 'Failed to update user status');
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openStatusDialog = (status: string) => {
    setNewStatus(status);
    setStatusDialog(true);
    handleMenuClose();
  };

  const showAlert = (type: 'success' | 'error', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const openEditDialog = () => {
    if (!selectedUser) return;
    setEditFormData({
      firstName: selectedUser.firstName,
      lastName: selectedUser.lastName,
      preferredName: selectedUser.preferredName || '',
      email: selectedUser.email,
      phoneNumber: selectedUser.phoneNumber,
      role: selectedUser.role,
      status: selectedUser.status
    });
    setEditDialog(true);
    handleMenuClose();
  };

  const openEditDialogForUser = (user: User) => {
    setEditFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      preferredName: user.preferredName || '',
      email: user.email,
      phoneNumber: user.phoneNumber,
      role: user.role,
      status: user.status
    });
    setSelectedUser(user);
    setEditDialog(true);
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${apiUrl}/admin/users/${selectedUser.id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'User updated successfully');
      setEditDialog(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Failed to update user:', error);
      showAlert('error', error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleCreateUser = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${apiUrl}/admin/users`,
        createFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'User created successfully');
      setCreateDialog(false);
      setCreateFormData({
        firstName: '',
        lastName: '',
        preferredName: '',
        email: '',
        phoneNumber: '',
        password: '',
        role: 'PASSENGER',
        status: 'ACTIVE'
      });
      fetchUsers();
    } catch (error: any) {
      console.error('Failed to create user:', error);
      showAlert('error', error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${apiUrl}/admin/users/${selectedUser.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'User banned successfully');
      setDeleteDialog(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      showAlert('error', error.response?.data?.message || 'Failed to ban user');
    }
  };

  const handlePermanentDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${apiUrl}/admin/users/${selectedUser.id}/permanent`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'User account permanently deleted');
      setPermanentDeleteDialog(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Failed to permanently delete user:', error);
      showAlert('error', error.response?.data?.message || 'Failed to permanently delete user');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING_VERIFICATION': return 'warning';
      case 'SUSPENDED': return 'error';
      case 'BANNED': return 'error';
      default: return 'default';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'DRIVER': return 'primary';
      case 'PASSENGER': return 'secondary';
      case 'ADMIN': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setCreateDialog(true)}
        >
          Create New User
        </Button>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleFilter}
              label="Role"
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="DRIVER">Driver</MenuItem>
              <MenuItem value="PASSENGER">Passenger</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="PENDING_VERIFICATION">Pending</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
              <MenuItem value="BANNED">Banned</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>User</TableCell>
                <TableCell>Member ID</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Trips</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                 users.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    onClick={() => openEditDialogForUser(user)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={user.profilePicture}
                          sx={{ width: 32, height: 32, cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchUserDetails(user.id);
                          }}
                        >
                          {user.firstName[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {user.preferredName || user.firstName} {user.lastName ? user.lastName.charAt(0) + '.' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {user.id.substring(0, 8)}...
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {user.memberId || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email}</Typography>
                      {user.emailVerified ? (
                        <Chip label="Verified" size="small" color="success" sx={{ mt: 0.5 }} />
                      ) : (
                        <Chip label="Not Verified" size="small" color="error" sx={{ mt: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {user.phoneNumber ? (
                        <Typography variant="body2">{user.phoneNumber}</Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Not Provided
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={user.role} size="small" color={getRoleColor(user.role)} />
                    </TableCell>
                    <TableCell>
                      <Chip label={user.status} size="small" color={getStatusColor(user.status)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        Driver: {user.totalTripsAsDriver}
                      </Typography>
                      <Typography variant="body2">
                        Passenger: {user.totalTripsAsPassenger}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        🚗 {user.driverRating?.toFixed(1) || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        🧑 {user.passengerRating?.toFixed(1) || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchUserDetails(user.id);
                        }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuClick(e, user);
                        }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={openEditDialog}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit User
        </MenuItem>
        <MenuItem onClick={() => openStatusDialog('ACTIVE')}>
          <CheckCircle fontSize="small" sx={{ mr: 1 }} /> Activate
        </MenuItem>
        <MenuItem onClick={() => openStatusDialog('SUSPENDED')}>
          <Block fontSize="small" sx={{ mr: 1 }} /> Suspend
        </MenuItem>
        <MenuItem onClick={() => { setDeleteDialog(true); handleMenuClose(); }}>
          <Delete fontSize="small" sx={{ mr: 1, color: 'error.main' }} /> Ban User
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setPermanentDeleteDialog(true); handleMenuClose(); }} sx={{ color: 'error.main' }}>
          <DeleteForever fontSize="small" sx={{ mr: 1 }} /> Delete Account Permanently
        </MenuItem>
      </Menu>

      {/* User Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          User Details
        </DialogTitle>
        <DialogContent>
          {userDetails && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={userDetails.profilePicture} sx={{ width: 80, height: 80 }}>
                  {userDetails.firstName[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {userDetails.preferredName || userDetails.firstName} {userDetails.lastName ? userDetails.lastName.charAt(0) + '.' : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userDetails.email}
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 'bold', display: 'block', mt: 0.5 }}>
                    Member ID: {userDetails.memberId || 'N/A'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip label={userDetails.role} size="small" color={getRoleColor(userDetails.role)} />
                    <Chip label={userDetails.status} size="small" color={getStatusColor(userDetails.status)} />
                  </Box>
                </Box>
              </Box>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>Statistics</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Trips as Driver</Typography>
                    <Typography variant="h6">{userDetails.totalTripsAsDriver}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Trips as Passenger</Typography>
                    <Typography variant="h6">{userDetails.totalTripsAsPassenger}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Driver Rating</Typography>
                    <Typography variant="h6">{userDetails.driverRating?.toFixed(2) || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Passenger Rating</Typography>
                    <Typography variant="h6">{userDetails.passengerRating?.toFixed(2) || 'N/A'}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>Account Information</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Typography variant="body2">Phone: {userDetails.phoneNumber || 'Not Provided'}</Typography>
                  <Typography variant="body2">
                    Email Verified: {userDetails.emailVerified ? '✅ Yes' : '❌ No'}
                  </Typography>
                  <Typography variant="body2">
                    Joined: {new Date(userDetails.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2">
                    Last Login: {userDetails.lastLoginAt ? new Date(userDetails.lastLoginAt).toLocaleDateString() : 'Never'}
                  </Typography>
                </Box>
              </Paper>

              {userDetails.vehicles && userDetails.vehicles.length > 0 && (
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>Vehicles ({userDetails.vehicles.length})</Typography>
                  {userDetails.vehicles.map((vehicle: any) => (
                    <Typography key={vehicle.id} variant="body2">
                      {vehicle.year} - {vehicle.color} - {vehicle.licensePlate}
                    </Typography>
                  ))}
                </Paper>
              )}

              {userDetails.documents && userDetails.documents.length > 0 && (
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>Documents ({userDetails.documents.length})</Typography>
                  {userDetails.documents.map((doc: any) => (
                    <Box key={doc.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{doc.type}</Typography>
                      <Chip label={doc.status} size="small" color={getStatusColor(doc.status)} />
                    </Box>
                  ))}
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
        <DialogTitle>Update User Status</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to change {selectedUser?.preferredName || selectedUser?.firstName} {selectedUser?.lastName ? selectedUser?.lastName.charAt(0) + '.' : ''}'s status to <strong>{newStatus}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateStatus} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              label="First Name"
              value={editFormData.preferredName || editFormData.firstName}
              onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Last Name"
              value={editFormData.lastName}
              onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={editFormData.email}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={editFormData.phoneNumber}
              onChange={(e) => setEditFormData({ ...editFormData, phoneNumber: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editFormData.role}
                label="Role"
                onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
              >
                <MenuItem value="PASSENGER">Passenger</MenuItem>
                <MenuItem value="DRIVER">Driver</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editFormData.status}
                label="Status"
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="PENDING_VERIFICATION">Pending Verification</MenuItem>
                <MenuItem value="SUSPENDED">Suspended</MenuItem>
                <MenuItem value="BANNED">Banned</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              label="First Name"
              value={createFormData.preferredName || createFormData.firstName}
              onChange={(e) => setCreateFormData({ ...createFormData, firstName: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Last Name"
              value={createFormData.lastName}
              onChange={(e) => setCreateFormData({ ...createFormData, lastName: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={createFormData.email}
              onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={createFormData.phoneNumber}
              onChange={(e) => setCreateFormData({ ...createFormData, phoneNumber: e.target.value })}
              placeholder="1234567890"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={createFormData.password}
              onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={createFormData.role}
                label="Role"
                onChange={(e) => setCreateFormData({ ...createFormData, role: e.target.value })}
              >
                <MenuItem value="PASSENGER">Passenger</MenuItem>
                <MenuItem value="DRIVER">Driver</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={createFormData.status}
                label="Status"
                onChange={(e) => setCreateFormData({ ...createFormData, status: e.target.value })}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="PENDING_VERIFICATION">Pending Verification</MenuItem>
                <MenuItem value="SUSPENDED">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained" color="primary">
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Ban User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to <strong>ban</strong> {selectedUser?.preferredName || selectedUser?.firstName} {selectedUser?.lastName ? selectedUser?.lastName.charAt(0) + '.' : ''}?
            This action will set their status to BANNED and they won't be able to use the platform.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Note: This is a soft ban. The account data is preserved. To permanently delete the account, use "Delete Account Permanently".
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} variant="contained" color="error">
            Ban User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog open={permanentDeleteDialog} onClose={() => setPermanentDeleteDialog(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>⚠️ Permanently Delete Account</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>WARNING: This action cannot be undone!</strong>
          </Alert>
          <Typography gutterBottom>
            You are about to <strong>permanently delete</strong> the account of:
          </Typography>
          <Typography variant="h6" sx={{ my: 2 }}>
            {selectedUser?.preferredName || selectedUser?.firstName} {selectedUser?.lastName ? selectedUser?.lastName.charAt(0) + '.' : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Email: {selectedUser?.email}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Phone: {selectedUser?.phoneNumber}
          </Typography>
          <Typography sx={{ mt: 2 }}>
            This will <strong>permanently remove all user data</strong> including:
          </Typography>
          <Box component="ul" sx={{ mt: 1 }}>
            <li>Profile information</li>
            <li>Ride history</li>
            <li>Payment records</li>
            <li>Messages and reviews</li>
            <li>All associated data</li>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Only use this option for GDPR compliance requests or legitimate account deletion requests.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermanentDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handlePermanentDeleteUser} variant="contained" color="error" startIcon={<DeleteForever />}>
            Permanently Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
